import React from 'react'

type CapacityIndicatorProps = {
  variant?: 'capacity' | 'time'
  units?: number
  maxUnits?: number
  tierColor: string
  onDark?: boolean
  breakdown?: string
  example?: string
  timeLabel?: string
  timeSub?: string
}

export function CapacityIndicator({
  variant = 'capacity',
  units = 0,
  maxUnits = 8,
  tierColor,
  onDark = false,
  breakdown,
  example,
  timeLabel,
  timeSub,
}: CapacityIndicatorProps) {
  const labelColor = onDark ? 'text-gray-300' : 'text-gray-500'
  const strongColor = onDark ? 'text-white' : 'text-gray-900'
  const trackBg = onDark ? 'rgba(255,255,255,0.12)' : '#EEF2F7'
  const tickEmpty = onDark ? 'rgba(255,255,255,0.18)' : '#D7DEE7'

  if (variant === 'time') {
    return (
      <div className="flex flex-col gap-2">
        <span className={`text-xs font-medium uppercase tracking-wide ${labelColor} font-['Poppins']`}>
          Monthly capacity
        </span>
        <div
          className="inline-flex flex-col items-start gap-0.5 self-start rounded-lg px-3 py-2"
          style={{ backgroundColor: `${tierColor}1F` }}
        >
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: tierColor }} />
            <span className="text-sm font-bold font-['Poppins']" style={{ color: tierColor }}>
              {timeLabel}
            </span>
          </div>
          {timeSub && (
            <span className={`text-xs ${labelColor} font-['Roboto'] pl-4`}>{timeSub}</span>
          )}
        </div>
      </div>
    )
  }

  const lCells = Math.max(1, Math.round(units / 4))
  const filledPct = Math.min(100, (units / maxUnits) * 100)
  const ticks = Array.from({ length: maxUnits }, (_, i) => i < units)

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <span className={`text-xs font-medium uppercase tracking-wide ${labelColor} font-['Poppins']`}>
          Monthly delivery capacity
        </span>
        <span className={`text-xs font-bold font-['Poppins'] ${strongColor}`}>{units} S</span>
      </div>

      <div
        className="relative h-9 w-full overflow-hidden rounded-lg"
        style={{ backgroundColor: trackBg }}
        role="img"
        aria-label={`${units} S of delivery capacity per month, equal to ${breakdown || ''}`}
      >
        <div
          className="absolute inset-y-0 left-0 flex"
          style={{ width: `${filledPct}%` }}
        >
          {Array.from({ length: lCells }).map((_, i) => (
            <div
              key={i}
              className="flex h-full flex-1 items-center justify-center"
              style={{
                backgroundColor: tierColor,
                borderRight: i < lCells - 1 ? '2px solid rgba(255,255,255,0.45)' : 'none',
              }}
            >
              <span className="text-xs font-bold text-white font-['Poppins']">L</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-1" aria-hidden="true">
        {ticks.map((filled, i) => (
          <span
            key={i}
            className="h-1.5 flex-1 rounded-full"
            style={{ backgroundColor: filled ? tierColor : tickEmpty }}
          />
        ))}
      </div>

      {breakdown && <span className={`text-xs ${labelColor} font-['Roboto']`}>{breakdown}</span>}
      {example && <span className={`text-xs ${labelColor} font-['Roboto'] italic leading-snug`}>In a typical month: {example}</span>}
    </div>
  )
}